import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, interval } from 'rxjs';
import { AuthenticationService } from './AuthenticationService';
import { defaultMessageQuery, MemberMessagePage, MemberMessageService } from './MemberMessageService';

export interface MemberMessagePreviewState extends MemberMessagePage { Loading: boolean; Error: boolean; }
const emptyPreview = (): MemberMessagePreviewState => ({ Items: [], TotalCount: 0, UnreadCount: 0, Loading: true, Error: false });

/** One account-scoped source for the shell badge and dashboard preview. No reads are acknowledged here. */
@Injectable({ providedIn: 'root' })
export class MemberMessageNotificationService implements OnDestroy {
  private readonly state = new BehaviorSubject<MemberMessagePreviewState>(emptyPreview());
  readonly state$ = this.state.asObservable();
  private identity = '';
  private request?: Subscription;
  private polling?: Subscription;
  private readonly userUpdates: Subscription;

  constructor(private api: MemberMessageService, private auth: AuthenticationService) {
    this.userUpdates = auth.onUserUpdate().subscribe(() => this.syncAccount());
    this.syncAccount();
  }

  syncAccount(): void {
    const id = this.auth.token && !this.auth.userHasRole('admin') ? this.auth.userData?.User?.Id : null;
    const identity = id ? `${id}:${this.auth.isImpersonating()}` : '';
    if (this.identity === identity) return;
    this.request?.unsubscribe();
    this.polling?.unsubscribe();
    this.identity = identity;
    this.state.next({ ...emptyPreview(), Loading: !!identity });
    if (!identity) return;
    this.refresh();
    this.polling = interval(60000).subscribe(() => this.refresh());
  }

  refresh(): void {
    if (!this.identity || this.request && !this.request.closed) return;
    const identity = this.identity;
    this.request = this.api.list({ ...defaultMessageQuery, pageSize: 5 }).subscribe({
      next: result => {
        if (identity === this.identity) this.state.next({ ...result, Loading: false, Error: false });
      },
      error: () => {
        if (identity === this.identity) this.state.next({ ...this.state.value, Loading: false, Error: true });
      }
    });
  }

  /** Cancel a potentially stale count request after a successful read, then fetch the server count. */
  readChanged(): void {
    this.request?.unsubscribe();
    this.refresh();
  }

  ngOnDestroy(): void {
    this.userUpdates.unsubscribe();
    this.request?.unsubscribe();
    this.polling?.unsubscribe();
  }
}
